import { useCallback, useState, useRef } from "react";
import type { KeyboardEvent } from "react";
import { useLocation, useNavigate } from "react-router";
import { useChatStore } from "../../stores/chat-store";
import { useChatUIStore } from "../../stores/chat-ui-store";
import { useChatSocketContext } from "../../contexts/useChatSocketContext";
import { MessageList } from "./MessageList";
import { ConnectionBanner } from "./ConnectionBanner";
import { IconButton } from "../ui/IconButton";
import { cn } from "../../lib/utils";
import { AIOrbAvatar } from "../ui/AIOrbAvatar";
import MinimizeSquare from "~icons/solar/minimize-square-3-linear";
import MaximizeSquare from "~icons/solar/maximize-square-3-linear";
import ArrowUpLinear from "~icons/solar/arrow-up-linear";
import StopBold from "~icons/solar/stop-bold";

/** Floating chat that follows the user across pages; hidden on /chat itself. */
export function PersistentChatPanel() {
  const location = useLocation();
  const navigate = useNavigate();

  const messages = useChatStore((s) => s.messages);
  const isStreaming = useChatStore((s) => s.isStreaming);
  const threadId = useChatStore((s) => s.threadId);

  const isPanelMinimized = useChatUIStore((s) => s.isPanelMinimized);
  const setPanelMinimized = useChatUIStore((s) => s.setPanelMinimized);
  const togglePanelMinimized = useChatUIStore((s) => s.togglePanelMinimized);

  const {
    connectionState,
    connected,
    sendMessage,
    resendLastMessage,
    stopGeneration,
    toolAnimation,
  } = useChatSocketContext();

  const [inputValue, setInputValue] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Hide entirely on the dedicated /chat route — full-screen ChatPage owns it there
  const isChatRoute = location.pathname === "/chat";

  const canSend =
    inputValue.trim().length > 0 && connected && !isStreaming;

  const handleSend = useCallback(() => {
    const text = inputValue.trim();
    if (!text || !connected || isStreaming) return;
    sendMessage(text);
    setInputValue("");
    textareaRef.current?.focus();
  }, [inputValue, connected, isStreaming, sendMessage]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (canSend) handleSend();
      }
    },
    [canSend, handleSend]
  );

  const handleExpandToFullView = useCallback(() => {
    const search = messages.length > 0 ? `?t=${threadId}` : "";
    navigate(`/chat${search}`);
  }, [navigate, messages.length, threadId]);

  if (isChatRoute) return null;

  // Minimized: floating pill in the corner
  if (isPanelMinimized) {
    const hasMessages = messages.length > 0;
    return (
      <button
        type="button"
        onClick={() => setPanelMinimized(false)}
        className="fixed bottom-24 right-6 z-40 flex size-14 items-center justify-center rounded-full bg-white shadow-pop ring-1 ring-black/[0.06] transition-transform hover:scale-105 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
        aria-label="Open Smart Scout chat"
        title="Smart Scout"
      >
        <AIOrbAvatar size={44} staticCircle />
        {hasMessages && (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-semibold leading-none text-white ring-2 ring-white">
            {messages.length > 99 ? "99+" : messages.length}
          </span>
        )}
      </button>
    );
  }

  return (
    <div
      className="fixed bottom-24 right-6 z-40 flex w-[min(420px,calc(100vw-3rem))] flex-col overflow-hidden rounded-3xl border border-black/10 bg-white shadow-pop"
      style={{ height: "min(620px, calc(100vh - 8rem))" }}
      role="complementary"
      aria-label="Chat panel"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-black/[0.06] px-4 py-3">
        <div className="flex items-center gap-2">
          <AIOrbAvatar size={28} staticCircle />
          <span className="text-sm font-bold text-ink">Smart Scout</span>
        </div>
        <div className="flex items-center gap-1">
          <IconButton
            icon={MaximizeSquare}
            aria-label="Open full chat view"
            title="Open full view"
            size="sm"
            onClick={handleExpandToFullView}
          />
          <IconButton
            icon={MinimizeSquare}
            aria-label="Minimize chat panel"
            title="Minimize"
            size="sm"
            onClick={togglePanelMinimized}
          />
        </div>
      </div>

      <div className="px-3 pt-2">
        <ConnectionBanner state={connectionState} />
      </div>

      {/* Messages area */}
      <div className="flex min-h-0 flex-1 flex-col px-2">
        {messages.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
            <AIOrbAvatar size={64} staticCircle />
            <div>
              <p className="text-sm font-bold text-ink">Start a conversation</p>
              <p className="mt-1 text-xs text-ink-muted">
                Your messages stay visible as you move between pages.
              </p>
            </div>
          </div>
        ) : (
          <MessageList
            messages={messages}
            onRetry={resendLastMessage}
            toolAnimation={toolAnimation}
          />
        )}
      </div>

      {/* Slim input — text-only. Use the full /chat view for files & @mentions. */}
      <div className="border-t border-black/[0.06] p-3">
        <div className="flex items-end gap-2 rounded-2xl border border-black/10 bg-white p-1.5 pl-2 shadow-card transition-colors focus-within:border-primary/40">
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={!connected}
            placeholder={connected ? "Type a message…" : "Connecting…"}
            rows={1}
            className="flex-1 resize-none bg-transparent px-1 py-1.5 text-sm text-ink outline-none placeholder:text-ink-faint disabled:cursor-not-allowed"
            aria-label="Chat message"
          />
          {isStreaming ? (
            <IconButton
              icon={StopBold}
              aria-label="Stop generating"
              variant="primary"
              size="sm"
              onClick={stopGeneration}
              className="size-8 shrink-0 bg-ink hover:bg-ink/85"
            />
          ) : (
            <IconButton
              icon={ArrowUpLinear}
              aria-label="Send message"
              variant="primary"
              size="sm"
              onClick={handleSend}
              disabled={!canSend}
              className={cn(
                "size-8 shrink-0",
                !canSend && "bg-black/[0.06] text-ink-faint shadow-none"
              )}
            />
          )}
        </div>
      </div>
    </div>
  );
}
